import React, { useState, useEffect } from "react";
import { adventuresVideos } from "../../data/adventures.videos";
import LoadingSpinner from "../Elements/LoadingSpinner";
import { useTranslation } from "react-i18next";
import {
  CaptionOverlay,
  CaptionText,
  Header,
  HeaderContent,
  HeaderSubtitle,
  HeaderTitle,
  StyledVideo,
  ThumbnailImage,
  VideoColumn,
  VideoContainer,
  VideoGrid,
  ViewWrapper,
} from "../../style/adventures.view.styles";
import VideoDetailOverlay from "../Elements/VideoDetailOverlay";
import SSAIntelligenceOverlay from "../Elements/SSAIntelligenceOverlay";

export default function AdventuresView({ formData , language}) {
  const [currentLayout, setCurrentLayout] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [videos, setVideos] = useState([]);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [hoveredVideoIndex, setHoveredVideoIndex] = useState(null);
  const [overlayLocked, setOverlayLocked] = useState(false);
  const [videoPosition, setVideoPosition] = useState(null);
  const [activeOverlay, setActiveOverlay] = useState(null);

  const { t } = useTranslation("adventures");

  useEffect(() => {
    const fetchVideos = () => {
      const videosList = adventuresVideos.flatMap((country) => country.videos);
      setVideos(videosList);
    };
    fetchVideos();
    setHasLoaded(true);
  }, []);

  useEffect(() => {
    if (formData.country) {
      setHasLoaded(false);
      const videosList = adventuresVideos
        .filter(
          (item) =>
            t(item.country).toLowerCase() === formData.country.toLowerCase()
        )
        ?.flatMap((item) => item?.videos);
      setVideos(videosList);
      setHasLoaded(true);
    }
    // eslint-disable-next-line
  }, [formData.country]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (overlayLocked && hoveredVideoIndex !== null) {
        return;
      }
      setCurrentLayout((prev) => (prev + 1) % 2);
    }, 20000);

    return () => clearInterval(interval);
  }, [overlayLocked, hoveredVideoIndex]);

  const getVideoOrder = (layoutIndex) => {
    const orders = [
      [0, 1, 2, 3],
      [4, 5, 6, 7],
    ];
    return orders[layoutIndex];
  };

  const videoOrder = getVideoOrder(currentLayout);

  const getVideoPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      right: rect.right + window.scrollX,
      bottom: rect.bottom + window.scrollY,
      width: rect.width,
      height: rect.height,
    };
  };
  
  const handleOverlayTrigger = (event, videoData, videoIndex) => {
    if (activeOverlay) return;
    setHoveredVideo(videoData);
    setHoveredVideoIndex(videoIndex);
    setVideoPosition(getVideoPosition(event));
    if (!overlayLocked) {
      setOverlayLocked(true);
      setActiveOverlay("videoDetail");
    }
  }

  return (
    <ViewWrapper>
      <Header>
        <HeaderContent animate={hasLoaded}>
          <HeaderTitle>{t("elevate_experience")}</HeaderTitle>
          <HeaderSubtitle>{t("elevate_experience_message")}</HeaderSubtitle>
        </HeaderContent>
      </Header>

      {!hasLoaded && <LoadingSpinner />}

      {hasLoaded && (
        <VideoGrid>
          {videoOrder?.length > 1 &&
            videoOrder.map((videoIndex, position) => {
              const videoData = videos[videoIndex];
              return (
                <VideoColumn
                  key={`${videoIndex}-${position}`}
                  position={position}
                  animate={hasLoaded}
                >
                  <VideoContainer>
                    {!isLoaded && (
                      <ThumbnailImage
                        src={videoData?.thumbnail}
                        alt={videoData?.caption}
                      />
                    )}
                    <StyledVideo
                      autoPlay
                      muted
                      loop
                      playsInline
                      src={videoData?.video}
                      onLoadedData={() => setIsLoaded(true)}
                    />
                    <CaptionOverlay
                      onMouseEnter={(event)=> handleOverlayTrigger(event, videoData, videoIndex)}
                      onClick={(event)=> handleOverlayTrigger(event, videoData, videoIndex)}
                    >
                      <CaptionText>{t(videoData?.caption)}</CaptionText>
                    </CaptionOverlay>
                  </VideoContainer>
                </VideoColumn>
              );
            })}

          <VideoDetailOverlay
            video={hoveredVideo}
            isVisible={activeOverlay === "videoDetail"}
            videoIndex={hoveredVideoIndex}
            videoPosition={videoPosition}
            onClose={() => {
              setHoveredVideo(null);
              setHoveredVideoIndex(null);
              setVideoPosition(null);
              setOverlayLocked(false);
              setActiveOverlay(null);
            }}
            onTalkToExpert={() => setActiveOverlay("expert")}
          />

          <SSAIntelligenceOverlay
            video={hoveredVideo}
            isVisible={activeOverlay === "expert"}
            videoIndex={hoveredVideoIndex}
            onClose={() => {
              setHoveredVideo(null);
              setHoveredVideoIndex(null);
              setVideoPosition(null);
              setOverlayLocked(false);
              setActiveOverlay(null);
            }}
            videoPosition={videoPosition}
            language={language}
          />
        </VideoGrid>
      )}
    </ViewWrapper>
  );
}
